import { type PropsWithChildren, type ReactNode, useRef } from 'react';
import { type AriaToastProps, useToast as useReactAriaToast } from '@react-aria/toast';
import type { QueuedToast, ToastState } from '@react-stately/toast';
import cx from 'clsx';
import IconCheckmarkRoundedFilled from '@repo/monochrome-icons/icon-checkmark-rounded-filled';
import IconCrossOutlined from '@repo/monochrome-icons/icon-cross-outlined';
import IconCrossRoundedFilledSmall from '@repo/monochrome-icons/icon-cross-rounded-filled-small';
import IconInfoRoundedFilled from '@repo/monochrome-icons/icon-info-rounded-filled';
import { Button } from '#components/button/button';
import styles from './toast-context.module.css';

export interface ToastData {
  type?: 'info' | 'success' | 'error';
  text: string;
}

interface ToastComponentProps extends AriaToastProps<ToastData> {
  state: ToastState<ToastData>;
  toast: QueuedToast<ToastData>;
  text: string;
  type?: 'info' | 'success' | 'error';
  className?: string;
}

/**
 * Toast component that displays a message to the user. This is visually identical to toast.tsx in the parent folder,
 * but uses the React Aria's toast implementation under the hood. Apart from that, it also improves the accessibility
 * comparing to toast.tsx.
 */
export function ToastComponent({
  type = 'info',
  text,
  state,
  ...props
}: PropsWithChildren<ToastComponentProps>): ReactNode {
  const ref = useRef(null);
  const { toastProps, contentProps, titleProps, closeButtonProps } = useReactAriaToast(
    props,
    state,
    ref
  );

  function onClose(): void {
    props.toast.onClose?.();
    state.close(props.toast.key);
  }

  return (
    <div
      className={cx(styles.toast, styles[type], props.className)}
      data-test-toast=""
      data-test-type={type}
      ref={ref}
      {...toastProps}
    >
      <div {...contentProps} className={styles.toastContent}>
        <div className={styles.iconContainer}>
          {type === 'info' && <IconInfoRoundedFilled className={styles.icon} />}
          {type === 'success' && <IconCheckmarkRoundedFilled className={styles.icon} />}
          {type === 'error' && <IconCrossRoundedFilledSmall className={styles.icon} />}
        </div>
        <p className={cx('body-standard', styles.text)} {...titleProps}>
          {text}
        </p>
        <Button
          className={styles.close}
          iconOnly
          onPress={onClose}
          size="small"
          variant="tertiary"
          {...closeButtonProps}
        >
          <IconCrossOutlined />
        </Button>
      </div>
    </div>
  );
}
